import {
    h, Component, ComponentType, ComponentChild, createRef, RefObject
} from "preact";
import { Keyboard } from "../../foundation/enums";
import { cmdKey, ctrlOrWinKey } from "../../foundation/utils/keyboard";
import Navigable from "./Navigable";
import NavigationDirection from "./NavigationDirection";

interface NavigableComponentProps {
    giveNavigation: (direction: NavigationDirection, element: Navigable) => void;
}

interface WithNavigableComponentInjectedProps {
    navigableRef: RefObject<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}

type WithNavigableComponentProps = NavigableComponentProps & WithNavigableComponentInjectedProps;

abstract class NavigableComponent<P extends NavigableComponentProps> extends Component<P> implements Navigable {
    protected navigableRef = createRef<HTMLElement>();
    public mounted = false;

    gainNavigation = () => {
        this.navigableRef.current!.focus();
        this.navigableRef.current!.click();
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        if (cmdKey(ev) || ev.altKey || ev.shiftKey || ctrlOrWinKey(ev)) {
            return;
        }

        const { giveNavigation } = this.props;
        let preventDefault = true;

        switch (ev.key) {
            case Keyboard.Key.ArrowDown:
                giveNavigation(NavigationDirection.Down, this);
                break;

            case Keyboard.Key.ArrowUp:
                giveNavigation(NavigationDirection.Up, this);
                break;

            default:
                preventDefault = false;
        }

        if (preventDefault) {
            ev.preventDefault();
        }
    };

    handleFocus = () => {
        document.addEventListener("keydown", this.handleKeyDown);
    };

    handleBlur = () => {
        document.removeEventListener("keydown", this.handleKeyDown);
    };

    override componentDidMount() {
        this.mounted = true;
        this.navigableRef.current!.addEventListener("focus", this.handleFocus);
        this.navigableRef.current!.addEventListener("blur", this.handleBlur);
    }

    override componentWillUnmount() {
        this.mounted = false;
        this.navigableRef.current!.removeEventListener("focus", this.handleFocus);
        this.navigableRef.current!.removeEventListener("blur", this.handleBlur);
        document.removeEventListener("keydown", this.handleKeyDown);
    }
}

function withNavigableComponent<P extends NavigableComponentProps>(WrappedComponent: ComponentType<P>) {
    return class extends NavigableComponent<Omit<P, keyof WithNavigableComponentInjectedProps>> {
        render(): ComponentChild {
            return <WrappedComponent {...this.props as P} navigableRef={this.navigableRef}/>;
        }
    };
}

export default NavigableComponent;
export {
    withNavigableComponent,
    WithNavigableComponentProps,
    NavigableComponentProps
};
